import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import styled from 'styled-components';
import FilmDataService from "../services/FilmService";
import ReactMarkdown from 'react-markdown'
import IFilmData from '../types/Film';
import { HeaderImageContext } from '../context/HeaderImageContext'
import { placeholderImageMobileContentHeader, placeholderImageDesktopContentHeader } from "../config.json";
import { isMobileView } from '../utils/windowSizeHelper';

// Styled component with subtle, always-visible clickable affordances
const FilmCard = styled.div`
  padding: 16px 18px;
  margin-bottom: 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: background-color 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background-color: rgba(166, 124, 124, 0.12);
    
    .view-text {
      color: #5a3e3e;
    }
  }

  @media only screen and (max-width: 450px) {
    padding: 14px 12px;
    margin-bottom: 8px;
  }
`;

const FilmTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  .film-title {
    color: #5a3e3e;
    text-decoration: underline;
    text-decoration-color: rgba(90, 62, 62, 0.4);
    text-underline-offset: 3px;
    font-weight: 500;
    letter-spacing: 2px;
    font-size: 1.1em;
  }
  
  .view-text {
    font-size: 0.85em;
    color: #A67C7C;
    transition: color 0.2s ease;
    white-space: nowrap;
    font-weight: 500;
    
    @media only screen and (max-width: 450px) {
      font-size: 0.75em;
    }
  }
`;

const FilmCredits = styled.div`
  margin-top: 4px;
  color: rgba(48, 24, 24, 0.75);
  font-size: 0.95em;
  
  p {
    margin: 0;
  }
  
  @media only screen and (max-width: 450px) {
    font-size: 0.85em;
  }
`;

const FilmsList: React.FC = () => {
  const [films, setFilms] = useState<Array<IFilmData>>([]);
  const { setHeaderImage } = useContext(HeaderImageContext)
  const [isMobile, setIsMobile] = useState<boolean>(isMobileView());
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileView());
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const image = { name: 'films', url: placeholderImageDesktopContentHeader }
    setHeaderImage(image);
    retrieveFilms();
  }, [setHeaderImage]);

  const retrieveFilms = () => {
    FilmDataService.getAll()
      .then((response: any) => {
        const films = response.data.data.sort((a, b) => (a.id < b.id) ? 1 : -1)
        setFilms(films);
      })
      .catch((e: Error) => {
        console.log('error fetching films');
        console.log(e);
      });
  };

  const gotoFilm = (id: string) => {
    navigate(`/film/${id}`, { replace: false });
  };

  // Handle keyboard navigation for accessibility
  const handleKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      gotoFilm(id);
    }
  };

  return (
    <>
      <div
        className="content-header"
        style={{ backgroundImage: isMobile ? 'url(' + placeholderImageMobileContentHeader + ')' : 'none' }}
      >
        <h2 style={{ marginLeft: isMobile ? undefined : '18px' }}>FILMS</h2>
   
        <div className="film-list-container">
          {films &&
            films.map((film) => (
              <FilmCard 
                key={film.id?.toString()}
                onClick={() => gotoFilm(film.id || '1')}
                onKeyDown={(e) => handleKeyDown(e, film.id || '1')}
                role="button"
                tabIndex={0}
                aria-label={`View details for ${film.attributes.title}`}
              >
                <FilmTitleRow>
                  <span className="film-title">{film.attributes.title}</span>
                  <span className="view-text">View →</span>
                </FilmTitleRow>
                <FilmCredits>
                  <ReactMarkdown children={film.attributes.subtitle || ''} />
                </FilmCredits>
              </FilmCard>
            ))}
        </div>
      </div>
    </>
  );
};

export default FilmsList;